import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  clearComposerError,
  createCommunityPostThunk,
  fetchCommunityTopicsThunk,
} from '../../redux/communitySlice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { CommunityPost, CommunityPostCreatePayload } from '../../types';
import CommunityComposer from '../../components/CommunityComposer';
import './ComposePage.css';

const ComposePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { topics, topicsStatus, composerStatus, composerError } = useAppSelector((state) => state.community);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawRepostId = searchParams.get('repost');
  const initialRepostId = rawRepostId ? rawRepostId.trim() || null : null;

  const handleRepostCleared = useCallback(() => {
    if (!searchParams.has('repost')) {
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('repost');
    const nextSearch = nextParams.toString();
  navigate(`/app/community/compose${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [navigate, searchParams]);

  useEffect(() => {
    if (topicsStatus === 'idle') {
      dispatch(fetchCommunityTopicsThunk());
    }
  }, [dispatch, topicsStatus]);

  const handleSubmit = async (payload: CommunityPostCreatePayload): Promise<CommunityPost> => {
    const created = await dispatch(createCommunityPostThunk(payload)).unwrap();
    return created;
  };

  return (
    <div className="community-compose">
      <section className="community-compose__hero">
        <div className="community-compose__hero-text">
          <span className="community-compose__hero-badge">发布中心</span>
          <h1>发布新帖</h1>
          <p>分享你的节气调养思路，与草本社区的伙伴建立有温度的连接。</p>
          <ul>
            <li>使用 #话题 自动关联分类话题广场</li>
            <li>上传图片或视频，更生动地呈现你的案例</li>
            <li>发布前草稿会实时保存在当前设备</li>
          </ul>
        </div>
        <div className="community-compose__hero-card">
          <strong>草稿自动保存中</strong>
          <span>任何改动都会保留 30 天，随时回来继续创作。</span>
        </div>
      </section>
      <CommunityComposer
        topics={topics}
        onSubmit={handleSubmit}
        submitting={composerStatus === 'loading'}
        error={composerError}
        onClearError={() => dispatch(clearComposerError())}
  onPostCreated={(post) => navigate(`/app/community/status/${post.post_id}`)}
        initialRepostId={initialRepostId}
        onRepostCleared={handleRepostCleared}
      />
    </div>
  );
};

export default ComposePage;
